import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { get, post } from "../../services/api";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

function CategoriesTab() {
  const token = localStorage.getItem("token");
  const [triggerRerender, setTriggerRerender] = useState(false);
  const [categoriesData, setCategoriesData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userId = localStorage.getItem("userId");

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      id: null,
      name: "",
      description: "",
      id: 0,
    },
  });

  // Fetch categories
  const getCategories = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(`QAForm/categories?includeInactive=false`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCategoriesData(response);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  // Save/Update category
  const saveCategory = async (data) => {
    setIsSubmitting(true);
    data = {
      ...data,
      createdBy: userId,
    };
    if (!token) {
      console.error("Unauthorized.");
      toast.error("Unauthorized access");
      setIsSubmitting(false);
      return;
    }

    try {
      const response =
        data.id === 0
          ? await post("QAForm/categories", data, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
          : await post("QAForm/UpdateCategory", data, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

      if (response.isSuccess) {
        toast.success(response.message || "Category saved successfully!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: null,
          name: "",
          description: "",
        });
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error("Failed to save category");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit category
  const handleEdit = async (row) => {
    try {
      reset({
        id: row.id,
        name: row.name,
        description: row.description,
      });
    } catch (error) {
      console.error("Error editing category:", error);
      toast.error("Failed to load category data");
    }
  };

  // Delete category
  const handleDelete = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this category?"
    );

    if (confirmDelete) {
      try {
        const response = await get(`QAForm/delete-categories?id=${rowId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.isSuccess) {
          toast.success(response.message || "Category deleted successfully!");
          setTriggerRerender((prev) => !prev);
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
        toast.error("Failed to delete category");
      }
    }
  };

  // Input change handler
  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  // Export to Excel
  const exportToExcel = () => {
    const filteredData = categoriesData.map((row, index) => ({
      "Sr No": index + 1,
      "Category Name": row.name,
      Description: row.description,
      "Question Count": row.questionCount,
      Status: row.isActive ? "Active" : "Inactive",
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Question Categories");
    XLSX.writeFile(workbook, "QuestionCategories.xlsx");
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = "Question Categories";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = 20;
    doc.text(title, titleX, titleY);

    const headers = [
      "Sr No",
      "Category Name",
      "Description",
      "Question Count",
      "Status",
    ];

    const bodyData = categoriesData.map((row, index) => [
      index + 1,
      row.name,
      row.description || "-",
      row.questionCount,
      row.isActive ? "Active" : "Inactive",
    ]);

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 40 },
        2: { cellWidth: 60 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
      },
    });

    doc.save("QuestionCategories.pdf");
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 60,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Category Name",
        size: 200,
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 300,
      },
      {
        accessorKey: "questionCount",
        header: "Question Count",
        size: 120,
      },
      {
        accessorKey: "isActive",
        header: "Status",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isActive ? "bg-success" : "bg-danger"
            }`}
          >
            {row.original.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original)}
              data-bs-toggle="tooltip"
              title="Edit Category"
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDelete(row.original.id)}
              data-bs-toggle="tooltip"
              title="Delete Category"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Fetch data on mount and when triggerRerender changes
  useEffect(() => {
    getCategories();
  }, [triggerRerender]);

  // Initialize tooltips
  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, [categoriesData]);

  return (
    <div className="row g-3">
      {/* Left Side - Form */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">
              {getValues("id") ? "Edit Category" : "Add New Category"}
            </h5>
          </div>
          <div className="card-body">
            <form
              className="row g-3 new-form-style"
              onSubmit={handleSubmit(saveCategory)}
            >
              <input type="hidden" {...register("id")} />

              <div className="col-md-12">
                <label className="form-label form-label-new">
                  Category Name<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("name", {
                    required: "Category name is required.",
                    maxLength: {
                      value: 100,
                      message: "Maximum of 100 characters allowed.",
                    },
                  })}
                  type="text"
                  className="form-control"
                  placeholder="Enter category name"
                  onChange={handleChangeOfInputWithLengthCheck("name")}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name.message}</small>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label form-label-new">Description</label>
                <textarea
                  {...register("description", {
                    maxLength: {
                      value: 500,
                      message: "Maximum of 500 characters allowed.",
                    },
                  })}
                  className="form-control"
                  rows="3"
                  placeholder="Enter category description"
                  onChange={handleChangeOfInputWithLengthCheck("description")}
                />
                {errors.description && (
                  <small className="text-danger">
                    {errors.description.message}
                  </small>
                )}
              </div>

              <div className="col-12 mt-3">
                <button
                  className="btn btn-primary btn-sm"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : getValues("id")
                    ? "Update Category"
                    : "Add Category"}
                </button>

                {getValues("id") && (
                  <button
                    className="btn btn-secondary btn-sm ms-2"
                    type="button"
                    onClick={() =>
                      reset({ id: null, name: "", description: "" })
                    }
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Data Table */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Categories List</h5>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={exportToExcel}
                data-bs-toggle="tooltip"
                title="Export to Excel"
              >
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={exportToPDF}
                data-bs-toggle="tooltip"
                title="Export to PDF"
              >
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="table-container fixed-last-column-table">
              <CustomDatatableGrid
                columns={columns}
                data={categoriesData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                  columnVisibility: {
                    description: false,
                    isActive: false,
                  },
                  pagination: {
                    pageSize: 10,
                    pageIndex: 0,
                  },
                }}
                enableRowSelection={false}
                enableColumnFilters={true}
                enableGlobalFilter={true}
                enablePagination={true}
                enableSorting={true}
                enableColumnResizing={true}
                muiTablePaperProps={{
                  elevation: 0,
                }}
                muiTableContainerProps={{
                  sx: {
                    maxHeight: "400px",
                  },
                }}
                renderTopToolbarCustomActions={() => (
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold">
                      Total: {categoriesData.length}
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CategoriesTab;
